import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseResourceOptions<T, R = T> {
  initial?: T;
  onError?: (err: Error) => void;
  transform?: (raw: R) => T;
}

export interface UseResourceResult<T> {
  data: T;
  loading: boolean;
  reloading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
  setData: React.Dispatch<React.SetStateAction<T>>;
}

/**
 * Standardized async-resource hook.
 *
 *   const { data, loading, error, reload, setData } = useResource(
 *     () => apiClient.get<Post[]>('/posts/posts/'),
 *     [],
 *     { initial: [], transform: (raw) => Array.isArray(raw) ? raw : raw.results },
 *   );
 *
 * - `loading` is true on first fetch only; reloads surface via `reloading`.
 * - Stale responses are dropped when the component unmounts or deps change.
 * - `setData` is exposed for optimistic updates.
 */
export default function useResource<T, R = T>(
  fetcher: () => Promise<R>,
  deps: ReadonlyArray<unknown> = [],
  options: UseResourceOptions<T, R> = {},
): UseResourceResult<T> {
  const { initial, onError, transform } = options;
  const [data, setData] = useState<T>(initial as T);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isFirstRef = useRef(true);
  const mountedRef = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Set mountedRef = true on every mount and false on every unmount.
  // React 19 StrictMode double-invokes effects — without the explicit
  // set-true here the cleanup from the first mount leaves mountedRef
  // false during the second mount, so every state setter after a
  // fetch resolves is skipped and "Loading…" never clears.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const run = useCallback(async (): Promise<void> => {
    const first = isFirstRef.current;
    if (first) setLoading(true);
    else setReloading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      if (!mountedRef.current) return;
      setData((transform ? transform(result) : (result as unknown as T)));
    } catch (err) {
      if (!mountedRef.current) return;
      const wrapped = err instanceof Error ? err : new Error(String(err));
      setError(wrapped);
      if (onError) onError(wrapped);
    } finally {
      if (mountedRef.current) {
        if (first) setLoading(false);
        else setReloading(false);
        isFirstRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, reloading, error, reload: run, setData };
}
